/**
 * Error translation system for user-friendly error messages in Polish
 */

// Known API error patterns and their translations
interface ErrorPattern {
  pattern: RegExp | string;
  message: string;
  extractDetails?: (match: RegExpMatchArray | null, rawError: string) => string | null;
}

// InPost API error translations
const INPOST_ERRORS: ErrorPattern[] = [
  {
    pattern: /building_number.*required/i,
    message: 'Brak numeru budynku w adresie. Upewnij się, że adres zawiera numer domu.',
  },
  {
    pattern: /street.*required/i,
    message: 'Brak nazwy ulicy w adresie.',
  },
  {
    pattern: /city.*required/i,
    message: 'Brak nazwy miasta w adresie.',
  },
  {
    pattern: /post_code.*required|postal_code.*required/i,
    message: 'Brak kodu pocztowego w adresie.',
  },
  {
    pattern: /phone.*required|phone.*invalid/i,
    message: 'Brak lub nieprawidłowy numer telefonu odbiorcy.',
  },
  {
    pattern: /email.*required|email.*invalid/i,
    message: 'Brak lub nieprawidłowy adres email odbiorcy.',
  },
  {
    pattern: /name.*required|first_name.*required|last_name.*required/i,
    message: 'Brak imienia lub nazwiska odbiorcy.',
  },
  {
    pattern: /target_point.*required|target_point.*invalid/i,
    message: 'Brak lub nieprawidłowy kod paczkomatu. Sprawdź numer punktu odbioru.',
  },
  {
    pattern: /target_point.*not.*found|point.*not.*exist/i,
    message: 'Podany paczkomat nie istnieje. Sprawdź czy kod jest poprawny.',
  },
  {
    pattern: /weight.*exceeded|weight.*too.*heavy/i,
    message: 'Przekroczono maksymalną wagę przesyłki.',
  },
  {
    pattern: /dimensions.*exceeded|size.*too.*large/i,
    message: 'Przekroczono maksymalne wymiary przesyłki.',
  },
  {
    pattern: /insufficient.*funds|balance.*insufficient/i,
    message: 'Niewystarczające środki na koncie InPost. Doładuj konto.',
  },
  {
    pattern: /unauthorized|authentication.*failed|invalid.*token/i,
    message: 'Błąd autoryzacji InPost. Sprawdź klucz API w ustawieniach.',
  },
  {
    pattern: /rate.*limit|too.*many.*requests/i,
    message: 'Zbyt wiele zapytań do InPost. Spróbuj ponownie za chwilę.',
  },
  {
    pattern: /service.*unavailable|maintenance/i,
    message: 'Serwis InPost jest tymczasowo niedostępny. Spróbuj później.',
  },
  {
    pattern: /duplicate.*shipment|already.*exists/i,
    message: 'Przesyłka dla tego zamówienia już istnieje.',
  },
  {
    pattern: /offer.*(is.*not.*available|not.*available)|offer_is_not_available/i,
    message: 'Wybrana oferta InPost wygasła albo nie jest już dostępna. Ponów utworzenie przesyłki, aby pobrać nową ofertę. Jeśli błąd się powtórzy, sprawdź wybraną usługę, Paczkomat, adres odbiorcy oraz gabaryt i wagę paczki.',
  },
  {
    pattern: /validation_failed/i,
    message: 'Błąd walidacji danych przesyłki.',
  },
  {
    // Raw InPost error body when serialized as plain text, e.g.
    // "There are some validation errors. Check details object for more info."
    // Without this rule the user sees surowy angielski komunikat.
    pattern: /there are some validation errors/i,
    message: 'Błąd walidacji danych przesyłki po stronie InPost (sprawdź adres, telefon, paczkomat lub wybraną usługę).',
  },
  {
    pattern: /country.*not.*supported/i,
    message: 'Wysyłka do wybranego kraju nie jest obsługiwana.',
  },
  {
    pattern: /cod.*not.*allowed|cash.*on.*delivery.*disabled/i,
    message: 'Opcja pobrania (COD) nie jest dostępna dla wybranej usługi.',
  },
  {
    pattern: /insurance.*exceeded/i,
    message: 'Przekroczono maksymalną wartość ubezpieczenia.',
  },
  // Label generation errors
  {
    pattern: /CustomerIsNotParcelOwner/i,
    message: 'Nie można pobrać etykiety — przesyłka jest już w trakcie doręczenia lub została dostarczona i nie należy do nadawcy.',
  },
  {
    pattern: /ShipmentNotConfirmed/i,
    message: 'Przesyłka nie została opłacona. Najpierw opłać przesyłkę, a następnie pobierz etykietę.',
  },
  {
    pattern: /ShipmentCancelled/i,
    message: 'Przesyłka została anulowana. Nie można pobrać etykiety.',
  },
  {
    pattern: /ShipmentExpired/i,
    message: 'Przesyłka wygasła. Utwórz nową przesyłkę.',
  },
  {
    pattern: /label_generation_failed/i,
    message: 'Nie udało się wygenerować etykiety. Sprawdź status przesyłki w panelu InPost.',
  },
];

// Fakturownia API error translations
const FAKTUROWNIA_ERRORS: ErrorPattern[] = [
  {
    pattern: /buyer_tax_no.*invalid|nip.*invalid/i,
    message: 'Nieprawidłowy numer NIP nabywcy.',
  },
  {
    pattern: /buyer_name.*required/i,
    message: 'Brak nazwy nabywcy faktury.',
  },
  {
    pattern: /positions.*required|positions.*empty/i,
    message: 'Faktura musi zawierać co najmniej jedną pozycję.',
  },
  {
    pattern: /invoice.*not.*found/i,
    message: 'Nie znaleziono faktury w systemie Fakturownia.',
  },
  {
    pattern: /unauthorized|api.*token.*invalid/i,
    message: 'Błąd autoryzacji Fakturownia. Sprawdź token API w ustawieniach.',
  },
  {
    pattern: /department.*not.*found/i,
    message: 'Nie znaleziono działu/oddziału w Fakturownia.',
  },
  {
    pattern: /correction.*not.*allowed/i,
    message: 'Nie można wystawić korekty do tej faktury.',
  },
  {
    pattern: /already.*paid|payment.*registered/i,
    message: 'Faktura jest już oznaczona jako opłacona.',
  },
  {
    pattern: /quota.*exceeded|limit.*reached/i,
    message: 'Przekroczono limit faktur w planie Fakturownia.',
  },
  {
    pattern: /ksef.*error|ksef.*failed/i,
    message: 'Błąd podczas wysyłki do KSeF. Sprawdź dane faktury.',
  },
  {
    pattern: /duplicate.*invoice|number.*exists/i,
    message: 'Faktura o tym numerze już istnieje.',
  },
];

// WooCommerce API error translations
const WOOCOMMERCE_ERRORS: ErrorPattern[] = [
  {
    pattern: /consumer_key.*invalid|consumer_secret.*invalid/i,
    message: 'Nieprawidłowe dane dostępowe do sklepu WooCommerce.',
  },
  {
    pattern: /rest_forbidden|permission.*denied/i,
    message: 'Brak uprawnień do API sklepu. Sprawdź uprawnienia klucza API.',
  },
  {
    pattern: /store.*not.*found|shop.*not.*found/i,
    message: 'Nie można połączyć się ze sklepem. Sprawdź adres URL.',
  },
  {
    pattern: /order.*not.*found/i,
    message: 'Nie znaleziono zamówienia w sklepie.',
  },
  {
    pattern: /connection.*refused|connection.*timeout/i,
    message: 'Nie można połączyć się ze sklepem. Sprawdź czy sklep jest dostępny.',
  },
  {
    pattern: /ssl.*certificate|https.*error/i,
    message: 'Błąd certyfikatu SSL sklepu. Sprawdź konfigurację HTTPS.',
  },
];

// General/Network error translations
const GENERAL_ERRORS: ErrorPattern[] = [
  {
    pattern: /network.*error|fetch.*failed|econnrefused/i,
    message: 'Błąd połączenia sieciowego. Sprawdź połączenie z internetem.',
  },
  {
    pattern: /timeout|timed.*out/i,
    message: 'Przekroczono czas oczekiwania na odpowiedź serwera.',
  },
  {
    pattern: /server.*error|internal.*error|500/i,
    message: 'Błąd wewnętrzny serwera. Spróbuj ponownie później.',
  },
  {
    pattern: /bad.*request|400/i,
    message: 'Nieprawidłowe dane żądania.',
  },
  {
    pattern: /not.*found|404/i,
    message: 'Nie znaleziono żądanego zasobu.',
  },
  {
    pattern: /forbidden|403/i,
    message: 'Brak uprawnień do wykonania tej operacji.',
  },
  {
    pattern: /conflict|409/i,
    message: 'Konflikt danych. Odśwież stronę i spróbuj ponownie.',
  },
];

// Application-specific error translations
const APP_ERRORS: ErrorPattern[] = [
  {
    pattern: /no.*active.*stores/i,
    message: 'Brak aktywnych sklepów. Dodaj i aktywuj sklep w ustawieniach.',
  },
  {
    pattern: /no.*inpost.*credentials/i,
    message: 'Brak konfiguracji InPost. Dodaj dane API w ustawieniach sklepu.',
  },
  {
    pattern: /no.*fakturownia.*credentials/i,
    message: 'Brak konfiguracji Fakturownia. Dodaj dane API w ustawieniach sklepu.',
  },
  {
    pattern: /order.*has.*no.*products/i,
    message: 'Zamówienie nie zawiera produktów.',
  },
  {
    pattern: /shipment.*already.*exists/i,
    message: 'Przesyłka dla tego zamówienia już istnieje.',
  },
  {
    pattern: /invoice.*already.*exists/i,
    message: 'Faktura dla tego zamówienia już istnieje.',
  },
  {
    pattern: /cannot.*delete.*has.*orders/i,
    message: 'Nie można usunąć - istnieją powiązane zamówienia.',
  },
  {
    pattern: /status.*in.*use/i,
    message: 'Nie można usunąć statusu, który jest używany przez zamówienia.',
  },
];

/**
 * Extract details from InPost validation error
 */
function extractInPostDetails(errorString: string): string[] {
  const details: string[] = [];

  // Try to parse JSON from error message
  try {
    const jsonMatch = errorString.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0]);

      // Handle nested details structure
      if (parsed.details) {
        const extractFields = (obj: Record<string, unknown>, prefix = ''): void => {
          for (const [key, value] of Object.entries(obj)) {
            if (Array.isArray(value)) {
              value.forEach((item) => {
                if (typeof item === 'string') {
                  details.push(`${prefix}${key}: ${translateFieldError(item)}`);
                } else if (typeof item === 'object') {
                  extractFields(item as Record<string, unknown>, `${prefix}${key}.`);
                }
              });
            } else if (typeof value === 'object' && value !== null) {
              extractFields(value as Record<string, unknown>, `${prefix}${key}.`);
            } else if (typeof value === 'string') {
              details.push(`${prefix}${key}: ${translateFieldError(value)}`);
            }
          }
        };
        extractFields(parsed.details);
      }

      // Handle message field
      if (parsed.message && typeof parsed.message === 'string') {
        const translatedMessage = translateFieldError(parsed.message);
        if (!details.some(d => d.includes(translatedMessage))) {
          details.unshift(translatedMessage);
        }
      }
    }
  } catch {
    // Not valid JSON, ignore
  }

  return details;
}

/**
 * Translate common field-level error messages
 */
function translateFieldError(error: string): string {
  const fieldTranslations: Record<string, string> = {
    'required': 'pole wymagane',
    'is required': 'pole wymagane',
    'invalid': 'nieprawidłowa wartość',
    'is invalid': 'nieprawidłowa wartość',
    'too short': 'wartość zbyt krótka',
    'too long': 'wartość zbyt długa',
    'must be present': 'pole wymagane',
    'can\'t be blank': 'pole nie może być puste',
    'is not a number': 'musi być liczbą',
    'must be greater than': 'musi być większe niż',
    'must be less than': 'musi być mniejsze niż',
  };

  let translated = error;
  for (const [eng, pl] of Object.entries(fieldTranslations)) {
    translated = translated.replace(new RegExp(eng, 'gi'), pl);
  }

  return translated;
}

/**
 * Translate field names to Polish
 */
function translateFieldName(field: string): string {
  const fieldNames: Record<string, string> = {
    'building_number': 'numer budynku',
    'street': 'ulica',
    'city': 'miasto',
    'post_code': 'kod pocztowy',
    'postal_code': 'kod pocztowy',
    'phone': 'telefon',
    'email': 'email',
    'name': 'nazwa',
    'first_name': 'imię',
    'last_name': 'nazwisko',
    'company_name': 'nazwa firmy',
    'target_point': 'punkt odbioru',
    'receiver': 'odbiorca',
    'sender': 'nadawca',
    'address': 'adres',
    'weight': 'waga',
    'dimensions': 'wymiary',
    'parcels': 'paczki',
    'buyer_name': 'nazwa nabywcy',
    'buyer_tax_no': 'NIP nabywcy',
    'positions': 'pozycje',
  };

  // Handle nested field paths
  const parts = field.split('.');
  const translatedParts = parts.map(part => fieldNames[part] || part);
  return translatedParts.join(' → ');
}

/**
 * Format detailed error information
 */
function formatErrorDetails(details: string[]): string {
  if (details.length === 0) return '';

  return details
    .map(detail => {
      // Translate field names in the detail
      const fieldMatch = detail.match(/^([a-z_]+(?:\.[a-z_]+)*): (.+)$/i);
      if (fieldMatch) {
        const [, field, message] = fieldMatch;
        return `• ${translateFieldName(field)}: ${message}`;
      }
      return `• ${detail}`;
    })
    .join('\n');
}

export interface TranslatedError {
  title: string;
  message: string;
  details?: string;
  originalError?: string;
}

/**
 * Main error translation function
 */
export function translateError(error: unknown, context?: string): TranslatedError {
  // Convert error to string
  let errorString = '';
  let originalError = '';

  // Check for Axios-like error (has response.data) BEFORE instanceof Error,
  // because AxiosError extends Error and error.message is just "Request failed with status code XXX"
  if (error && typeof error === 'object' && 'response' in error) {
    const err = error as { response?: { data?: unknown; status?: number }; message?: string };
    if (err.response?.data) {
      const data = err.response.data;
      if (typeof data === 'object' && data !== null) {
        const dataObj = data as Record<string, unknown>;
        // Extract the error message from backend response format { error: "message" }
        if (typeof dataObj.error === 'string') {
          errorString = dataObj.error;
          originalError = errorString;
        } else {
          errorString = JSON.stringify(data);
          originalError = errorString;
        }
      } else if (typeof data === 'string') {
        errorString = data;
        originalError = data;
      } else {
        errorString = JSON.stringify(data);
        originalError = errorString;
      }
    } else if (err.message) {
      errorString = String(err.message);
      originalError = errorString;
    }
  } else if (error instanceof Error) {
    errorString = error.message;
    originalError = error.message;
  } else if (typeof error === 'string') {
    errorString = error;
    originalError = error;
  } else if (error && typeof error === 'object') {
    const err = error as Record<string, unknown>;
    if (err.response && typeof err.response === 'object') {
      const response = err.response as Record<string, unknown>;
      if (response.data && typeof response.data === 'object') {
        const data = response.data as Record<string, unknown>;
        // Extract the error message string directly from response.data.error
        if (typeof data.error === 'string') {
          errorString = data.error;
          originalError = data.error;
        } else {
          errorString = JSON.stringify(response.data);
          originalError = errorString;
        }
      } else if (response.data) {
        errorString = String(response.data);
        originalError = errorString;
      }
    } else if (err.message) {
      errorString = String(err.message);
      originalError = errorString;
    } else {
      errorString = JSON.stringify(error);
      originalError = errorString;
    }
  }

  // Determine error source and context
  const isInPostError = /inpost|shipment|paczkomat|przesyłk|etykiet/i.test(errorString) ||
                        context?.toLowerCase().includes('inpost') ||
                        context?.toLowerCase().includes('shipment') ||
                        context?.toLowerCase().includes('label');
  const isFakturowniaError = /fakturownia|invoice|faktur/i.test(errorString) ||
                             context?.toLowerCase().includes('fakturownia') ||
                             context?.toLowerCase().includes('invoice');
  const isWooCommerceError = /woocommerce|woo|sklep|store|sync/i.test(errorString) ||
                             context?.toLowerCase().includes('woocommerce') ||
                             context?.toLowerCase().includes('sync');

  // Select appropriate error patterns based on context
  let patterns: ErrorPattern[] = [...GENERAL_ERRORS, ...APP_ERRORS];
  let title = 'Błąd';

  if (isInPostError) {
    patterns = [...INPOST_ERRORS, ...patterns];
    title = 'Błąd InPost';
  } else if (isFakturowniaError) {
    patterns = [...FAKTUROWNIA_ERRORS, ...patterns];
    title = 'Błąd Fakturownia';
  } else if (isWooCommerceError) {
    patterns = [...WOOCOMMERCE_ERRORS, ...patterns];
    title = 'Błąd synchronizacji';
  }

  // Try to match error patterns
  for (const { pattern, message } of patterns) {
    const regex = typeof pattern === 'string' ? new RegExp(pattern, 'i') : pattern;
    if (regex.test(errorString)) {
      const details = isInPostError ? extractInPostDetails(errorString) : [];
      return {
        title,
        message,
        details: formatErrorDetails(details) || undefined,
        originalError: details.length > 0 ? undefined : originalError,
      };
    }
  }

  // Try to extract InPost-specific details even if no pattern matched
  if (isInPostError) {
    const details = extractInPostDetails(errorString);
    if (details.length > 0) {
      return {
        title,
        message: 'Błąd podczas tworzenia przesyłki. Sprawdź dane adresowe.',
        details: formatErrorDetails(details),
      };
    }
  }

  // No pattern matched - return cleaned up original error
  return {
    title,
    message: cleanupErrorMessage(errorString) || 'Wystąpił nieoczekiwany błąd. Spróbuj ponownie.',
    originalError: undefined,
  };
}

/**
 * Clean up raw error message for display
 */
function cleanupErrorMessage(error: string): string {
  // Remove JSON formatting
  let cleaned = error
    .replace(/\{[\s\S]*\}/, '') // Remove JSON objects
    .replace(/\[[\s\S]*\]/, '') // Remove JSON arrays
    .replace(/status"?:\s*\d+,?/gi, '') // Remove status codes
    .replace(/error"?:\s*"?[^"]+,?/gi, '') // Remove error codes
    .replace(/"message"?:\s*"?/gi, '') // Remove message prefix
    .replace(/"/g, '') // Remove quotes
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();

  // Capitalize first letter
  if (cleaned.length > 0) {
    cleaned = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  }

  // Add period if missing
  if (cleaned.length > 0 && !cleaned.match(/[.!?]$/)) {
    cleaned += '.';
  }

  return cleaned;
}

/**
 * Create success message
 */
export interface SuccessMessage {
  title: string;
  message: string;
  details?: string;
}

export function createSuccessMessage(
  action: string,
  counts?: { success?: number; failed?: number; total?: number }
): SuccessMessage {
  const messages: Record<string, { title: string; message: string }> = {
    'sync': {
      title: 'Synchronizacja zakończona',
      message: 'Zamówienia zostały zsynchronizowane ze sklepem.'
    },
    'shipment': {
      title: 'Przesyłka utworzona',
      message: 'Przesyłka została pomyślnie utworzona.'
    },
    'shipments': {
      title: 'Przesyłki utworzone',
      message: 'Przesyłki zostały utworzone.'
    },
    'invoice': {
      title: 'Faktura wystawiona',
      message: 'Faktura została pomyślnie wystawiona.'
    },
    'invoices': {
      title: 'Faktury wystawione',
      message: 'Faktury zostały wystawione.'
    },
    'correction': {
      title: 'Korekta wystawiona',
      message: 'Korekta faktury została pomyślnie wystawiona.'
    },
    'label': {
      title: 'Etykieta pobrana',
      message: 'Etykieta przesyłki została pobrana.'
    },
    'labels': {
      title: 'Etykiety pobrane',
      message: 'Etykiety przesyłek zostały pobrane.'
    },
    'email': {
      title: 'Email wysłany',
      message: 'Wiadomość email została wysłana.'
    },
    'delete': {
      title: 'Usunięto',
      message: 'Element został pomyślnie usunięty.'
    },
    'save': {
      title: 'Zapisano',
      message: 'Zmiany zostały zapisane.'
    },
    'duplicate': {
      title: 'Zduplikowano',
      message: 'Element został zduplikowany.'
    },
    'paid': {
      title: 'Oznaczono jako opłacone',
      message: 'Faktura została oznaczona jako opłacona.'
    },
  };

  const baseMessage = messages[action] || { title: 'Sukces', message: 'Operacja zakończona pomyślnie.' };

  // Add counts if provided
  let details: string | undefined;
  if (counts) {
    const parts: string[] = [];
    if (counts.success !== undefined) parts.push(`Sukces: ${counts.success}`);
    if (counts.failed !== undefined && counts.failed > 0) parts.push(`Błędy: ${counts.failed}`);
    if (counts.total !== undefined) parts.push(`Łącznie: ${counts.total}`);
    if (parts.length > 0) {
      details = parts.join(' | ');
    }
  }

  return { ...baseMessage, details };
}
